import { PouchDoc } from './pouch';
import { IntakeFormState } from './intake-form-state';
import { FitnessPlan } from './fitness-plan';
import { FitnessState } from './user-state';
import { DateTime } from 'luxon';
import type { CouchUserInfo } from './couch-user';
import { isInGrace } from './license-reconciler';

/**
 * Effective number of concurrent client slots a coach can use, derived from
 * their user doc. A fixed-tier (app-store) coach carries the cap in
 * `coachTierData.tierCap` and has `coachMaxSlots = 0`; a legacy / admin coach
 * uses `coachMaxSlots`. This is the client-display counterpart to the server's
 * `resolveCoachCapability` FixedTier/Legacy cap — defined once in shared so the
 * tier cap can't be silently dropped on one side again (the bug this fixes: the
 * client read only `coachMaxSlots`, so an app-store tier coach showed 0 slots).
 *
 * A Paddle per-seat (web) coach carries an active `coachSeatData` and has NO
 * numeric cap (pay-per-seat is unlimited), so this returns 0 for them — the
 * numeric cap is meaningless for a seat coach. Use `isActiveCoach` for the
 * boolean "is this user a coach" gates (it recognizes coachSeatData), and
 * `hasActiveCoachSeat` where a seat coach needs the uncapped/"unlimited"
 * treatment (e.g. the coach-settings slot display + at-capacity check).
 *
 * Metered web coaches (`coachBillingData`, no `coachMaxSlots`) are lock-gated
 * rather than slot-capped, so they read 0 here and are not yet surfaced by
 * slot-based client UI — tracked as a separate follow-up.
 *
 * `now` is an optional injectable clock (defaults to `DateTime.utc()`); it
 * exists so unit tests can pin "now" against the tier's expiry.
 */
export function effectiveCoachSlots(
  user: Pick<CouchUserInfo, 'coachTierData' | 'coachMaxSlots'> | null | undefined,
  now?: DateTime,
): number {
  const tierData = user?.coachTierData;
  // if an app-store coach tier is active (still in grace), its tierCap is the cap
  if (tierData && isInGrace(tierData.subscriptionStatus, tierData.expiresDate_utc, (now ?? DateTime.utc()).toMillis())) {
    return tierData.tierCap ?? 0;
  }
  // else fall back to the legacy / admin-granted slot count
  return user?.coachMaxSlots ?? 0;
}

/**
 * Whether the user has an ACTIVE Paddle per-seat coaching subscription
 * (`coachSeatData` still in grace). This is the Paddle-rail counterpart to the
 * server's `resolveCoachCapability` PaddleSeat branch. A seat coach is uncapped
 * (pay-per-seat, no numeric slot cap), so `effectiveCoachSlots` returns 0 for
 * them — this helper is how client UI recognizes the uncapped case (unlimited
 * client slots, never at capacity).
 *
 * `now` is an optional injectable clock (defaults to `DateTime.utc()`).
 */
export function hasActiveCoachSeat(
  user: Pick<CouchUserInfo, 'coachSeatData'> | null | undefined,
  now?: DateTime,
): boolean {
  const seatData = user?.coachSeatData;
  if (!seatData) return false;
  return isInGrace(seatData.subscriptionStatus, seatData.expiresDate_utc, (now ?? DateTime.utc()).toMillis());
}

/**
 * Whether the user is a coach of ANY kind for client-side "is a coach" gates:
 * a fixed-tier (app-store) coach OR a legacy/admin coach (`effectiveCoachSlots
 * > 0`), OR a Paddle per-seat coach (active `coachSeatData`). This is the single
 * boolean recognition the coaching UI gates on, so no rail is silently dropped
 * on the client (the bug this fixes: a Paddle coach-seat user was granted
 * `coachSeat` server-side but the client, reading only `effectiveCoachSlots`,
 * never recognized them and hid the coaching UI).
 *
 * `now` is an optional injectable clock (defaults to `DateTime.utc()`).
 */
export function isActiveCoach(
  user: Pick<CouchUserInfo, 'coachTierData' | 'coachMaxSlots' | 'coachSeatData'> | null | undefined,
  now?: DateTime,
): boolean {
  return effectiveCoachSlots(user, now) > 0 || hasActiveCoachSeat(user, now);
}

/**
 * Status of a coach's client slot.
 * Pending: slot created, optionally has intake/plan data, waiting for client to claim.
 * Claimed: a real user has accepted the invite and received the plan.
 */
export enum CoachClientStatus {
  Pending = 'pending',
  Claimed = 'claimed',
}

/**
 * Who fills the intake form for this client slot.
 *
 * CoachFills (option A, today's flow): the coach fills the intake form
 * themselves on their own account, the app generates the plan, and the
 * claim link transfers the finished plan into the client's new account.
 *
 * ClientFills (option B, issue #3389): the coach sends a blank intake to
 * the client. The client claims an invite with no intake / plan attached,
 * fills the intake wizard themselves up to and including Goals, and is
 * held on a "your coach is finalizing your program" screen. The coach is
 * notified, enters the client's account via impersonation, reviews and
 * overwrites the answers, sets Training Volume and Energy Balance, and
 * generates the plan inside the client's account.
 *
 * Absent on a slot means CoachFills, so existing slots created before
 * #3389 landed keep working unchanged.
 */
export enum CoachIntakeMode {
  CoachFills = 'coach-fills',
  ClientFills = 'client-fills',
}

/**
 * Lifecycle of a ClientFills slot AFTER the client has claimed the invite.
 * Only used when CoachClientSlot.intakeMode === ClientFills, and only set
 * once the client claims the link — before claim the field is absent. The
 * field therefore only ever co-exists with CoachClientSlot.status === Claimed.
 *
 * Invited: the client has claimed the blank invite. Their account is in
 * WelcomePhase.CoachedIntake but they have not yet finished the intake
 * wizard. The server sets this value at claim time (coachClaimInvite),
 * together with status = Claimed and clientUid.
 *
 * Submitted: the client has finished the intake wizard (steps 1-8 through
 * Goals) and is held on the "your coach is finalizing your program" screen.
 * The coach has been notified and can now enter the client's account to
 * review the answers.
 *
 * Finalized: the coach has reviewed / overwritten the answers, set
 * Training Volume and Energy Balance, and generated the plan inside the
 * client's account. The holding screen clears and the client sees their
 * plan.
 */
export enum ClientIntakeStatus {
  Invited = 'invited',
  Submitted = 'submitted',
  Finalized = 'finalized',
}

/**
 * A client slot in the coach's database. Each client gets a separate document
 * with _id "coach-client/<uuid>". This avoids write contention when managing
 * multiple clients.
 *
 * The slot supports two onboarding paths, distinguished by `intakeMode`:
 *
 * - CoachFills (option A, default for slots created before #3389): the coach
 *   fills the intake form themselves on the slot. intakeFormState,
 *   fitnessPlan, and fitnessState are stored here; when the client claims
 *   the invite, those documents are copied into the client's own database
 *   by the coachClaimInvite server function.
 * - ClientFills (option B, #3389): the coach sends a blank invite. No
 *   intakeFormState / fitnessPlan / fitnessState live on the slot; the
 *   client fills the intake themselves inside their own account, and the
 *   coach reviews and finalizes via impersonation. The slot tracks the
 *   client's progress through `clientIntakeStatus`.
 *
 * Lifecycle: Created (Pending, intakeMode chosen) -> coach generates invite
 * link -> client claims (Claimed) -> (if ClientFills) clientIntakeStatus
 * walks Invited -> Submitted -> Finalized -> coach can remove (document
 * deleted).
 */
export interface CoachClientSlot extends PouchDoc {
  nickname: string;
  coachName?: string;
  /**
   * Whether the coach or the client fills the intake on this slot.
   * Absent means CoachFills so existing slots behave unchanged.
   */
  intakeMode?: CoachIntakeMode;
  /**
   * Lifecycle marker for ClientFills slots after the client has claimed
   * the invite. Walks Invited -> Submitted -> Finalized as the client and
   * coach do their parts. Absent on CoachFills slots, and also absent on
   * a ClientFills slot whose invite has not yet been claimed (the server
   * sets the field at claim time, not at link-generation time).
   */
  clientIntakeStatus?: ClientIntakeStatus;
  intakeFormState?: IntakeFormState;
  fitnessPlan?: FitnessPlan;
  fitnessState?: FitnessState;
  /** HMAC-signed invite token, stored for revocation checks. */
  claimToken?: string;
  status: CoachClientStatus;
  /** Firebase UID of the client who claimed this slot. */
  clientUid?: string;
  createdAt: string; // ISO 8601
  /**
   * When the client claimed this slot (#4690). Written once by
   * `coachClaimInvite`, at the moment the slot flips to Claimed, and never
   * touched again.
   *
   * It exists because the login-time repair of a client's missing
   * `coach-relationship/as-client` document needs to know how long ago the
   * claim happened, and nothing else records that. During a claim the client
   * writes that document to its own device first and only pushes it to the
   * server seconds later, so a repair running inside that window would create a
   * second, competing first revision of the same document. The repair therefore
   * waits until a claim is comfortably finished before acting.
   *
   * `coachData.grantedAt_utc` on the client's account record looks like it
   * would do the same job and does NOT: `buildMirroredCoachData` rewrites it
   * every time the coach's licence is mirrored, so it says when we last
   * refreshed the grant, not when the coaching started.
   *
   * Absent on every slot claimed before this shipped, and the repair reads an
   * absent value as "long ago" — which is exactly what lets it heal the
   * accounts that are already stranded.
   */
  claimedAt_utc?: string;
  /**
   * The claim attempt that claimed this slot (#6891). The client app makes a
   * random id once each time it starts a claim, and its automatic retries
   * resend that same id. So a later call carrying this id, from the same
   * client, with the same token, is the SAME claim retried after its answer was
   * lost, and `coachClaimInvite` answers it as a success. Reopening the link
   * starts a new claim with a new id, and is still refused.
   *
   * Not a secret: on its own it grants nothing. Absent on slots claimed before
   * #6891, and on slots claimed by an app version that sends no id.
   */
  claimAttemptId?: string;
  /**
   * Whether the coach has parked this claimed client (#4530).
   *
   * While it reads `Sleeping`, the coach cannot open this client, and the
   * client holds no coach-granted paid access — their `coachData` block stops
   * granting, exactly as it does when a coach's own subscription lapses.
   * Nothing else is affected: the client keeps their account, training plan
   * and history, no "former coach" record is written, and the slot is not
   * deleted. Waking sets it back to `Awake` and everything is as it was.
   *
   * ABSENT MEANS AWAKE. Every slot that existed before #4530 has no value
   * here, and so is awake — which is exactly what it was. Nothing has to be
   * migrated, and a slot that has simply never been parked never gains the
   * field at all.
   *
   * WAKING WRITES `Awake`. IT MUST NEVER DELETE THE FIELD, and this is the
   * single most important thing to know about this field.
   *
   * The reason is how two conflicting versions of a document are merged when
   * a sync conflict happens. `mergeConflictingDocs` in
   * `shared/src/deep-helpers.ts` decides each field separately, and its FIRST
   * rule is that if a field is present on one version and missing on the
   * other, the present one wins — before it ever looks at which version was
   * written more recently. So if waking deleted this field, a merge against
   * any older version that still said `Sleeping` would put the client
   * straight back to sleep: they would silently lose the paid access their
   * coach is paying for, with no error logged anywhere.
   *
   * Writing a real value on BOTH transitions avoids that completely. Two
   * versions that both carry a value are a genuine tie, and the merge settles
   * a tie by taking the one written most recently, which is the right answer.
   *
   * Only ever set on a slot whose `status` is Claimed — a pending invite has
   * no client behind it yet, so there is nobody to put to sleep, and the
   * server refuses the request.
   *
   * ONE SOURCE OF TRUTH: this field is the only place the sleeping state is
   * recorded. It is deliberately NOT copied onto the coach's roster document
   * or onto the client's own records, because two copies of the same fact
   * drift, and a drifted copy here would either strand a client without
   * access or hand one access they should not have.
   */
  sleepState?: CoachClientSleepState;
  /**
   * When `sleepState` last changed, so the coach's list can say how long a
   * client has been parked. Purely for display — no logic anywhere compares
   * two clients' timestamps to each other, and nothing decides whether a
   * client is asleep by looking here.
   *
   * Rewritten on both transitions, for the same merge reason as
   * `sleepState` above, and so that it always describes the state sitting
   * beside it rather than some earlier one.
   */
  sleepStateChangedAt?: string; // ISO 8601
  /**
   * Who last put this client to sleep. Only meaningful while `sleepState` is
   * `Sleeping`; on an awake slot it is a leftover from the last time they
   * were parked and nothing reads it.
   *
   * 'coach' — the coach chose to park them.
   * 'auto'  — the server did it, to bring the coach back within the client
   *           limit of their plan. Nothing writes this value yet; it is the
   *           automatic over-limit sleeping added by ticket #4454, and the
   *           field is defined here so the shape of a sleeping slot is
   *           decided in one place rather than two.
   *
   * The distinction exists because the coach must be able to tell the two
   * apart: a client the coach parked themselves needs no explanation, and a
   * client the server parked does.
   */
  sleptBy?: 'coach' | 'auto';
}

/**
 * Whether a coach has parked a claimed client (#4530).
 *
 * Absent on a slot means {@link CoachClientSleepState.Awake} — see the note
 * on `CoachClientSlot.sleepState`, which also explains why waking writes
 * `Awake` rather than removing the field.
 */
export enum CoachClientSleepState {
  Awake = 'awake',
  Sleeping = 'sleeping',
}

/**
 * Whether this client slot is currently asleep (#4530).
 *
 * Every call site goes through this helper rather than testing the field by
 * hand, so that how "asleep" is stored stays a decision made in one place —
 * and in particular so that no call site has to remember that a slot with no
 * value at all is awake.
 *
 * Note it deliberately does NOT also check that the slot is Claimed. The
 * server only ever parks a claimed slot, so a sleeping pending slot cannot
 * arise; if one somehow did, reporting it as asleep is the safe direction,
 * because everywhere this is consulted "asleep" means "give this client
 * nothing and count them nowhere".
 */
export function isSlotSleeping(slot: Pick<CoachClientSlot, 'sleepState'> | undefined): boolean {
  return slot?.sleepState === CoachClientSleepState.Sleeping;
}

/** Slot counts that a coach's client limit is measured against. */
export interface CoachSlotCounts {
  /** Slots with a real claimed client, excluding parked ones. */
  claimed: number;
  /**
   * Pending slots that have an invite token outstanding (waiting for a client
   * to claim). A Pending slot with NO token is a draft the coach has not
   * invited anyone to yet, so it does not hold a place.
   */
  outstandingInvites: number;
}

/**
 * Count the slots that take up a place on a coach's plan: claimed clients plus
 * outstanding (token-bearing) pending invites.
 *
 * A Pending slot counts only when it carries a `claimToken`, because an
 * outstanding INVITE is what holds a place — a token-less Pending slot is an
 * un-sent draft. This also makes the check at `coachCreateClaimToken` exact:
 * the slot being invited has no token yet, so it is naturally excluded from the
 * used count until its token is generated.
 *
 * **This lives in `shared/` because BOTH sides count.** The server enforces the
 * limit; the coach's own app shows "N client slots remaining" and decides
 * whether the Add Client button is available. Those two answers disagreeing is
 * how a coach gets told they have room and is then refused, so there is one
 * definition rather than one per side (#4530 R8).
 *
 * SLEEPING CLIENTS (#4530): a claimed client the coach has parked is not
 * counted at all. That is the whole point of parking — a parked client costs
 * the coach nothing and receives nothing, so parking one frees room for a new
 * client.
 *
 * READ THIS BEFORE CHANGING THE RULE — on the server this function has THREE
 * consumers and one of them charges real money:
 *
 *  1. `coachCreateClaimToken.ts` — the app-store plan's client limit. Freeing a
 *     place here is the point of the feature.
 *  2. `getCoachBillingState.ts` — a display of how many places are in use.
 *  3. `coach-seat-sync-helpers.ts` — `computeCoachSeatQuantity` PATCHes the
 *     coach's REAL Paddle subscription quantity. Paddle is the web payment
 *     provider where a coach pays per client rather than buying a fixed-size
 *     plan.
 *
 * Consumer 3 is intended, not accidental (decided by the user on 2026-07-31): a
 * Paddle coach who parks a client stops paying for that place, exactly as they
 * would if they removed the client outright, but without destroying the
 * relationship. Do NOT "protect" the Paddle rail by special-casing it back out
 * — that was considered and rejected, because it would leave a Paddle coach
 * paying for a client who is receiving nothing.
 *
 * Because that billing effect is intended, whatever parks a client must also
 * PUSH the new count to Paddle in the same action. Changing the count without
 * pushing it is the real bug. `setSlotSleepingAsync` does that push.
 *
 * Pending slots are unaffected by parking: only a Claimed slot is ever parked,
 * because a pending invite has no client behind it yet.
 */
export function classifyCoachSlotsForCap(slots: CoachClientSlot[]): CoachSlotCounts {
  let claimed = 0;
  let outstandingInvites = 0;
  // for each slot: a Claimed slot counts unless the coach has parked that
  // client; a Pending slot counts only if it has an outstanding invite token
  for (const slot of slots) {
    if (slot.status === CoachClientStatus.Claimed) {
      if (isSlotSleeping(slot)) continue;
      claimed++;
    } else if (slot.status === CoachClientStatus.Pending && slot.claimToken) {
      outstandingInvites++;
    }
  }
  return { claimed, outstandingInvites };
}

/**
 * Which of a coach's clients to park so that they stop being over the client
 * limit of their plan (#4454).
 *
 * Returns the slots to park, in the order they should be parked. Returns an
 * empty array when the coach is already at or under their limit — which is the
 * overwhelmingly common case, since a coach only goes over the limit by moving
 * to a smaller plan or by waking somebody while already full.
 *
 * **The rule: newest relationships are parked first**, so the coach's
 * longest-standing clients survive. Only AWAKE CLAIMED slots are candidates: an
 * already-parked client takes up no place, and a pending invite has no client
 * behind it to park.
 *
 * **`preferSlotId` puts one slot at the front of the queue, and it is not
 * optional in the one case that needs it.** A coach is deliberately allowed to
 * wake a client even when doing so puts them over their limit — refusing would
 * trap a coach who has no way back under it. The consequence is applied
 * immediately afterwards by parking somebody again, and without this argument
 * the newest-first rule would park whichever client happens to be newest: an
 * unrelated third person who did nothing and asked for nothing, silently losing
 * the paid access their coach provides. Passing the just-woken slot here means
 * the wake simply does not stick, which is honest and understandable. The call
 * site that must pass it is the wake branch of `coachSetClientSleeping`.
 *
 * **Do not put effort into making this ordering "fairer", and do not add data
 * to the slot for it** (user decision, 2026-07-31: *"let's not get too torn
 * about sleeping the last clients by time — could be alphabetic, no one cares.
 * The coach should NOT get into this state — their problem"*). This is a safety
 * net for a state the coach chose to create. It is not an attempt to judge which
 * relationship matters more, and the coach can wake whoever they actually want
 * immediately afterwards. Any pick made here is a starting point, not a verdict.
 *
 * **The coach IS warned first, where the app is able to warn them.** Before a
 * plan change made inside our own app, a confirmation names the clients that
 * will be parked and lets the coach cancel. That warning cannot cover a plan
 * change made from a store's own subscription screen, or an Apple change
 * applied at a renewal a month later — which is exactly why the automatic
 * parking exists rather than prevention alone, and why the coach's client list
 * says WHY a client was parked.
 *
 * `createdAt` records when the COACH CREATED the slot, not when the client
 * accepted the invite, so a slot created early but claimed late counts as older
 * than the relationship really is. That inaccuracy is accepted rather than
 * fixed: there is no claim timestamp on the slot to use instead, and adding one
 * would not help, because every slot that already exists would not carry it.
 *
 * Ties break on the slot's `_id`, which is a `coach-client/<uuid>` assigned once
 * when the slot is created and never changed afterwards — so the same input
 * always produces the same answer, which is what makes this testable at all.
 *
 * This lives in `shared/` and is pure so that the server and the coach's own
 * app give the SAME answer. The warning a coach sees before changing plan names
 * the clients this function picks; deriving that list separately on the client
 * is how such a warning ends up naming different people from the ones who
 * actually get parked.
 */
export function pickSlotsToSleepForCap(
  slots: CoachClientSlot[],
  cap: number,
  preferSlotId?: string,
): CoachClientSlot[] {
  // A cap of zero or below means DO NOTHING — never "park everybody".
  //
  // Zero is not a hypothetical value: a terminal store event drops `tierCap` to
  // exactly zero, so the difference between refusing and clamping to zero here
  // is the difference between doing nothing and taking every single one of a
  // coach's clients away from them. A missing or nonsense cap gets the same
  // answer, for the same reason.
  //
  // The server helper that calls this already refuses on a zero cap before
  // reaching here, so this is the second lock on the same door. Do not delete
  // it on the grounds that the server checks first: this function is
  // deliberately in `shared/` and the coach's own app calls it too, from two
  // screens that can easily hold a coach record which is still loading or
  // belongs to a plan that has just ended.
  if (!Number.isFinite(cap) || cap <= 0) return [];
  const effectiveCap = Math.floor(cap);

  const awakeClaimed = slots.filter(
    (slot) => slot.status === CoachClientStatus.Claimed && !isSlotSleeping(slot),
  );
  const excess = awakeClaimed.length - effectiveCap;
  if (excess <= 0) return [];

  const ordered = [...awakeClaimed].sort((a, b) => {
    // if one of them is the slot the caller asked to park first, it wins
    // outright, whatever its age.
    if (preferSlotId) {
      if (a._id === preferSlotId && b._id !== preferSlotId) return -1;
      if (b._id === preferSlotId && a._id !== preferSlotId) return 1;
    }
    // newest first. a slot with no createdAt sorts as the OLDEST, so it is the
    // last thing picked — erring towards leaving a client alone when we cannot
    // tell how old the relationship is.
    const byNewest = (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    if (byNewest !== 0) return byNewest;
    // same timestamp: fall back to the permanent id so the answer never
    // depends on what order the database handed the slots back in.
    return (a._id ?? '').localeCompare(b._id ?? '');
  });

  return ordered.slice(0, excess);
}

/**
 * The most client slots one query for a coach's parked clients will return
 * (#4530).
 *
 * Set explicitly because Mango's own default is 25 — low enough that a coach
 * with a normal roster would get a SHORT answer, and a short answer here means
 * a parked client looks awake and is handed their access back. The largest
 * app-store coach plan caps clients in the tens, so 1000 is far above anything
 * real while still bounding the response.
 *
 * A reader that hits this limit must treat the answer as unusable rather than
 * as "that is all of them" — see {@link collectFullyParkedClientUids}.
 */
export const COACH_CLIENT_SLOT_QUERY_LIMIT = 1000;

/** One coach client slot, projected down to just what parking decisions need. */
export type CoachClientSlotSleepProjection =
  Pick<CoachClientSlot, 'clientUid' | 'sleepState' | 'status'>;

/**
 * One coach client slot, projected down to what rebuilding a client's
 * `coach-relationship/as-client` document needs (#4690).
 *
 * A superset of {@link CoachClientSlotSleepProjection}, so a reader that
 * fetches these fields can still hand the result straight to
 * {@link collectFullyParkedClientUids} without a second query.
 *
 * `_id` is in the list because the slot's own id IS the `clientSlotId` the
 * rebuilt document has to carry — the option-B claim flow addresses the slot
 * through it.
 */
export type CoachClientSlotLoginProjection =
  CoachClientSlotSleepProjection & Pick<CoachClientSlot, '_id' | 'coachName' | 'claimedAt_utc'>;

/**
 * Which of a coach's clients are parked, given every CLAIMED slot that coach
 * holds (#4530).
 *
 * **A client counts as parked only when EVERY slot the coach holds for them is
 * parked, and that is the whole reason this is not a one-line filter.** A coach
 * can end up with two slots for the same person — `coachClaimInvite` adds a
 * roster entry per claimed invite with no duplicate check — and the answer this
 * function gives decides whether that person keeps their paid access. Adding
 * the client to the set because ONE of two slots is parked would take access
 * away from somebody whose other slot says they are being coached normally.
 * Requiring all of them errs the other way: a coach who parks one of a
 * duplicated pair sees no effect until they park the other, which is visible
 * and fixable, unlike silently revoking a paying client's access.
 *
 * Pending slots are ignored: a pending invite has nobody behind it, so it can
 * neither hold access nor withhold it.
 *
 * Callers pass slots read straight from the coach's database, so this is
 * shared by the server and by the `tools/` repair command rather than written
 * out twice — the two must never give different answers to the same question.
 */
export function collectFullyParkedClientUids(slots: CoachClientSlotSleepProjection[]): Set<string> {
  const totalSlotsPerClient = new Map<string, number>();
  const parkedSlotsPerClient = new Map<string, number>();

  // for each claimed slot with a client behind it: count it, and count it again
  // separately if it is parked
  for (const slot of slots) {
    if (slot.status !== CoachClientStatus.Claimed) continue;
    const clientUid = slot.clientUid;
    if (!clientUid) continue;
    totalSlotsPerClient.set(clientUid, (totalSlotsPerClient.get(clientUid) ?? 0) + 1);
    if (isSlotSleeping(slot)) {
      parkedSlotsPerClient.set(clientUid, (parkedSlotsPerClient.get(clientUid) ?? 0) + 1);
    }
  }

  const fullyParked = new Set<string>();
  // for each client the coach holds a slot for: parked only if every one of
  // their slots is parked
  for (const [clientUid, parkedCount] of parkedSlotsPerClient) {
    if (parkedCount === totalSlotsPerClient.get(clientUid)) {
      fullyParked.add(clientUid);
    }
  }
  return fullyParked;
}

export const coachRelAsCoachId = 'coach-relationship/as-coach';
export const coachRelAsClientId = 'coach-relationship/as-client';

/**
 * Heartbeat document written by each side into the CLIENT's CouchDB database
 * to signal "who's online". The coach writes `coach-presence/coach` while
 * managing a client; the client writes `coach-presence/client` while their app
 * is open and they have a coach. "Online" is staleness-based — consumers treat
 * `(now - lastSeenUtc) > PRESENCE_ONLINE_THRESHOLD_MS` as offline, so when a
 * heartbeat writer stops, the icon on the other side disappears purely by
 * time. See `design-docs/coach-mode.md` → "Presence / Who's Online".
 */
export interface CoachPresenceDoc extends PouchDoc {
  lastSeenUtc: string;  // ISO 8601
  displayName: string;
}

export const COACH_PRESENCE_COACH_ID = 'coach-presence/coach';
export const COACH_PRESENCE_CLIENT_ID = 'coach-presence/client';
export const PRESENCE_ONLINE_THRESHOLD_MS = 120_000;

/**
 * Bidirectional link between coach and client databases. Exists as a singleton
 * in both databases with different fixed IDs:
 * - "coach-relationship/as-coach" in the coach's DB (lists connected clients)
 * - "coach-relationship/as-client" in the client's DB (references the coach)
 *
 * Both are initialized as empty singletons on database setup. The server
 * populates them during coachClaimInvite and cleans them up during coachDetachClient.
 */
/**
 * Record of a coaching relationship that has ended. Lives on the
 * client's `coach-relationship/as-client` doc. Used to render the
 * "Former coach" badge on UDEs in the client's userdb-* whose
 * `authorIdentityHash` matches `computeAuthorIdentityHash(coachUid)`.
 *
 * Lifecycle:
 * - Appended (or replaced-by-coachUid) on every detach where a current
 *   coach was set. Server-side mutation in coachDetachClient and
 *   coachClientSelfDetach (see Phase 3 PRD).
 * - Removed on re-claim of the same coach (client-side, in
 *   coach-claim.service.ts).
 */
/**
 * Non-localized literal substituted when `coachName` is missing at detach
 * time (server side) or when the resolver matches a UDE to a former coach
 * with no name in `formerCoaches[]` (client side, contract-violation
 * surface). Shared so server / client agree on the sentinel and a future
 * rename or localization is single-site. Currently English-only because
 * the app ships only `en.json`; if locales beyond `en` are added, swap to
 * a localized fallback key at the client render sites and keep this
 * server-side default unchanged (or migrate to a `null` sentinel and
 * resolve at render time).
 */
export const DEFAULT_COACH_NAME_FALLBACK = 'Coach';

export interface FormerCoachEntry {
  coachUid: string;
  /**
   * Display name of the coach at detach time. Persisted server-side from
   * `CoachClientSlot.coachName` (coach-side detach) or
   * `coach-relationship/as-client.coachName` (self-detach). Falls back to
   * `DEFAULT_COACH_NAME_FALLBACK` if neither source had a value at detach
   * time — see coachDetachClient.ts / coachClientSelfDetach.ts.
   *
   * The client renders this verbatim in the picker badge tooltip and the
   * edit-exercise info card. Client-side render helpers
   * (`formerCoachBadgeTooltip` in exercise-switch.ts and
   * `formerCoachBadgeBodyText` in edit-exercise-page.ts) additionally
   * coerce a missing/empty value at render time to the same constant and
   * surface a `trackSilentProblem` breadcrumb so contract violations are
   * visible. Coercing to a different localized fallback at render time
   * would require an additional sentinel (e.g. `null`) to distinguish
   * "real unset name" from "server fallback already substituted" and is
   * not worth the complexity for this rare path.
   */
  coachName: string;
  /** ISO 8601 timestamp of the detach event. */
  detachedAt: string;
  /**
   * Set true on every entry in `formerCoaches[]` the first time the
   * post-detach picker notice is shown after `coachUid` clears (single
   * write site in `exercise-switch.ionViewDidEnter`). The notice is
   * shown at most once per "currently-detached" state — not once per
   * detach event. Per PRD §6 design ("could also display a single
   * notice for the most-recent former coach only — simpler. The
   * per-entry flag accommodates both behaviors").
   */
  noticeShown?: boolean;
}

/**
 * Tier 1 in-app white-labeling (#3707). The three things a coach can brand the
 * client-facing app with: a logo, a brand name, and one accent color. Lives on
 * the CoachRelationship doc — on `as-coach` it is the coach's source of truth
 * (edited on the Branding page); on each client's `as-client` it is the
 * per-client copy the client app themes from.
 *
 * Only the one validated accent hex is stored. The hover / active / tint shade
 * family and the contrast-safe on-accent text color are NEVER persisted — they
 * are recomputed at runtime from `accentColorHex` (see shared/src/color-math.ts
 * `deriveBrandPalette`), so the derivation rules can change with no data
 * migration. See design-docs/coach-white-labeling.md.
 */
export interface CoachBranding {
  /** GCS download URL of the coach's logo image. */
  logoUrl?: string;
  /** GCS object path of the logo (kept so a re-upload can delete the old one). */
  logoPath?: string;
  /** The coach's brand / business name, e.g. "Apex Performance". */
  brandName?: string;
  /** The ONE coach-picked accent color, always stored validated as `#RRGGBB`. */
  accentColorHex?: string;
  /** ISO 8601 timestamp of the last branding edit. Drives fan-out idempotency. */
  updatedAt?: string;
}

export interface CoachRelationship extends PouchDoc {
  // in coach's DB: list of connected clients
  clients?: { clientUid: string; nickname: string }[];
  // in client's DB: reference to coach
  coachUid?: string;
  coachName?: string;
  /**
   * Tier 1 white-labeling branding (#3707). Present on both singletons: on
   * `as-coach` it is the coach's source of truth; on `as-client` it is the
   * per-client copy the client app themes from. Absent means "no branding —
   * fall back to the MH default everywhere".
   */
  branding?: CoachBranding;
  /**
   * In client's DB only. The id of the CoachClientSlot in the coach's DB
   * that this client claimed. Written at claim time for ClientFills slots
   * (#3389) so the client's app can address the slot when it submits the
   * intake (`intakeSubmitted` notification) and when the coach finalizes
   * (slot lifecycle flip from Submitted to Finalized). Absent for slots
   * claimed before #3389 landed.
   */
  clientSlotId?: string;
  /**
   * In client's DB only. When the claim finished handing this client over to
   * their coach (#4817).
   *
   * Written once, in the LAST stage of the claim's apply
   * (`applyClaimDataAsync` in coach-claim.service.ts), so any earlier stage
   * that throws leaves it absent. It rides to the server in the same push that
   * `processClaimAsync` verifies with `didPushReachServer`, so a push that
   * never arrived means the coach never sees it either. Together that makes its
   * presence proof that every document the claim writes actually landed.
   *
   * Absent means the handover is NOT proven — which is why the coach's
   * dashboard only treats absence as a warning for claims made after
   * `HANDOVER_MARKER_SINCE_UTC`; every client claimed before this field
   * existed has none.
   *
   * The login-time repair that rebuilds a missing coach link
   * (`repairCoachLinkIfMissingAsync`, #4690) deliberately does NOT set this.
   * It knows the relationship exists; it does not know the program copy landed.
   */
  handoverCompletedAt?: string; // ISO 8601

  // in client's DB: history of ended coaching relationships
  formerCoaches?: FormerCoachEntry[];
}
